import { FC } from 'react'
import classNames from 'classnames'
import { SolidColorType } from 'common/types/color-type'

export type IconName =
  | 'chevron-left'
  | 'chevron-right'
  | 'user'
  | 'account'
  | 'basket'
  | 'chevron-up'
  | 'chevron-down'
  | 'close'
  | 'close-circle'
  | 'privacy'
  | 'hamburger-menu'
  | 'phone'
  | 'star'
  | 'tick'
  | 'hoxton-mix-symbol'
  | 'powered-by-logo'
  | 'gift'
  | 'tag'
  | 'info'
  | 'envelope'
  | 'tick-circle'
  | 'thumbs-up'
  | 'bubble'
  | 'go-cardless'
  | 'padlock'
  | 'bag'
  | 'search'
  | 'arrow-down'
  | 'institution'
  | 'accounting'
  | 'tick-square'
  | 'data'
  | 'file-lock'
  | 'check'

export type IconType = React.DetailedHTMLProps<
  React.HTMLAttributes<SVGSVGElement>,
  SVGSVGElement
> & {
  name: IconName
  width?: number
  height?: number
  stroke?: SolidColorType
  fill?: SolidColorType
  fill2?: SolidColorType
  className?: string
}

const Icon: FC<IconType> = ({
  fill,
  fill2,
  height,
  name,
  stroke,
  width,
  ...rest
}) => {
  const className = classNames(
    !!stroke && `stroke-${stroke}`,
    !!fill && `text-${fill}`,
    rest.className,
  )
  switch (name) {
    case 'search':
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || 17.49}
            height={height || 17.49}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 17.49 17.49'
          >
            <path
              id='ic_search_24px'
              d='M15.5,14h-.79l-.28-.27a6.51,6.51,0,1,0-.7.7l.27.28v.79l5,4.99L20.49,19Zm-6,0A4.5,4.5,0,1,1,14,9.5,4.494,4.494,0,0,1,9.5,14Z'
              transform='translate(-3 -3)'
              fill={fill || '#1d1d1f'}
            />
          </svg>
        </>
      )
    case 'bag':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || 22.5}
            height={height || 28}
            viewBox='0 0 89.759 112.338'
          >
            <g id='shopping-bag-svgrepo-com' transform='translate(-48.55 0)'>
              <path
                id='Path_44343'
                data-name='Path 44343'
                d='M138.309,97.363l-6.464-72.853a2.77,2.77,0,0,0-2.79-2.535H115.427a22,22,0,0,0-44,0H57.8a2.8,2.8,0,0,0-2.79,2.535L48.55,97.363v.256c0,8.115,7.464,14.72,16.626,14.72h56.506c9.162,0,16.626-6.6,16.626-14.72ZM93.429,5.581a16.439,16.439,0,0,1,16.417,16.394H77.012A16.439,16.439,0,0,1,93.429,5.581Zm28.253,101.153H65.176c-6.046,0-10.976-4.023-11.045-9.022l6.232-70.156H71.432v9.79a2.79,2.79,0,1,0,5.581,0v-9.79h32.834v9.79a2.79,2.79,0,1,0,5.581,0v-9.79H126.5l6.232,70.179C132.658,102.711,127.728,106.734,121.683,106.734Z'
                transform='translate(0)'
                fill={fill || '#1d1d1f'}
              />
            </g>
          </svg>
        </>
      )
    case 'user':
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || 14}
            height={height || 16}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 14 16'
          >
            <path
              id='user-icon'
              d='M13,5A5,5,0,1,0,4.75,8.8,6.994,6.994,0,0,0,1,15a1,1,0,0,0,2,0,5,5,0,0,1,10,0,1,1,0,0,0,2,0,7,7,0,0,0-3.75-6.2A4.989,4.989,0,0,0,13,5ZM8,8a3,3,0,1,1,3-3A3,3,0,0,1,8,8Z'
              transform='translate(-1)'
              fill={fill || '#939393'}
              fillRule='evenodd'
            />
          </svg>
        </>
      )
    case 'account': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || 20}
            height={height || 20}
            viewBox='0 0 20 20'
          >
            <path
              fill={fill || '#FFFFFF'}
              d='M10 0a10 10 0 1 0 10 10A10 10 0 0 0 10 0Zm0 3a3 3 0 1 1-3 3 3 3 0 0 1 3-3Zm0 14.2a7.2 7.2 0 0 1-6-3.22c.03-1.99 4-3.08 6-3.08s5.97 1.09 6 3.08a7.2 7.2 0 0 1-6 3.22Z'
            />
          </svg>
        </>
      )
    }
    case 'phone': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || 18}
            height={height || 24}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 13.75 22'
          >
            <path
              id='smartphone-icon'
              d='M14,22H5.75A2.749,2.749,0,0,1,3,19.25V2.75A2.749,2.749,0,0,1,5.75,0H14a2.749,2.749,0,0,1,2.75,2.75v16.5A2.749,2.749,0,0,1,14,22ZM14,4.125H5.75v13.75H14Z'
              transform='translate(-3)'
              fill='#1d1d1f'
              fillRule='evenodd'
            />
          </svg>
        </>
      )
    }
    case 'powered-by-logo': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || 125}
            height={height || 36}
            viewBox='0 0 125 36'
            data-name='Group 8999'
          >
            <g data-name='Group 899'>
              <rect
                width='125.386'
                height='36.481'
                fill={fill || '#42185a'}
                data-name='Rectangle 995'
                rx='5'
              />
              <path
                fill={fill2 || 'white'}
                d='M51.269 16.31a2.327 2.327 0 0 1-1.955-.907h-.088q.088.841.088 1.021v2.722h-1.416v-9.6h1.142q.048.186.2.889h.074a2.212 2.212 0 0 1 1.979-1.01 2.306 2.306 0 0 1 1.938.9 4.063 4.063 0 0 1 .693 2.534 4.066 4.066 0 0 1-.71 2.538 2.327 2.327 0 0 1-1.945.913Zm-.343-5.731a1.468 1.468 0 0 0-1.229.493 2.553 2.553 0 0 0-.383 1.573v.21a3.108 3.108 0 0 0 .385 1.757 1.43 1.43 0 0 0 1.251.547 1.275 1.275 0 0 0 1.13-.6 3.1 3.1 0 0 0 .4-1.712 2.994 2.994 0 0 0-.394-1.691 1.313 1.313 0 0 0-1.16-.577Zm9.917 2.28a3.627 3.627 0 0 1-.835 2.541 2.992 2.992 0 0 1-2.325.913 3.186 3.186 0 0 1-1.646-.423 2.776 2.776 0 0 1-1.1-1.208 4.113 4.113 0 0 1-.384-1.823 3.6 3.6 0 0 1 .829-2.523 3.006 3.006 0 0 1 2.337-.907 2.927 2.927 0 0 1 2.283.928 3.585 3.585 0 0 1 .841 2.502Zm-4.836 0q0 2.3 1.7 2.3 1.682 0 1.682-2.3 0-2.277-1.694-2.277a1.445 1.445 0 0 0-1.289.589 3 3 0 0 0-.399 1.688Zm11.046 3.331-.859-3.1q-.156-.493-.565-2.289h-.054q-.348 1.622-.553 2.3l-.883 3.089h-1.562l-1.863-6.645h1.442l.847 3.274q.288 1.214.409 2.079h.036q.06-.439.183-1.006t.213-.85l1.009-3.5h1.55l.979 3.5q.09.294.225.9a6.933 6.933 0 0 1 .159.943h.048a18.436 18.436 0 0 1 .421-2.067l.859-3.274h1.42l-1.875 6.646Zm6.889.12a3.214 3.214 0 0 1-2.424-.9 3.432 3.432 0 0 1-.874-2.49 3.766 3.766 0 0 1 .811-2.561 2.8 2.8 0 0 1 2.229-.931 2.738 2.738 0 0 1 2.079.8 3.053 3.053 0 0 1 .763 2.2v.763h-4.429a2.182 2.182 0 0 0 .523 1.487 1.821 1.821 0 0 0 1.388.52 5.123 5.123 0 0 0 1.1-.111 5.748 5.748 0 0 0 1.09-.369v1.141a4.335 4.335 0 0 1-1.045.348 6.41 6.41 0 0 1-1.211.103Zm-.258-5.815a1.419 1.419 0 0 0-1.078.427 2.033 2.033 0 0 0-.484 1.244h3.016a1.839 1.839 0 0 0-.4-1.247 1.356 1.356 0 0 0-1.054-.424Zm7.2-1.069a3.4 3.4 0 0 1 .7.06l-.138 1.316a2.668 2.668 0 0 0-.625-.072 1.808 1.808 0 0 0-1.373.553 2 2 0 0 0-.526 1.436v3.471h-1.408V9.545h1.105l.186 1.172h.072a2.633 2.633 0 0 1 .862-.943 2.049 2.049 0 0 1 1.146-.349Zm3.976 6.884a3.214 3.214 0 0 1-2.424-.9 3.432 3.432 0 0 1-.874-2.49 3.766 3.766 0 0 1 .81-2.561 2.8 2.8 0 0 1 2.229-.931 2.738 2.738 0 0 1 2.079.8 3.053 3.053 0 0 1 .763 2.2v.763h-4.429a2.182 2.182 0 0 0 .523 1.487 1.821 1.821 0 0 0 1.388.52 5.123 5.123 0 0 0 1.1-.111 5.747 5.747 0 0 0 1.09-.369v1.141a4.335 4.335 0 0 1-1.045.348 6.41 6.41 0 0 1-1.21.103Zm-.258-5.816a1.419 1.419 0 0 0-1.078.427 2.033 2.033 0 0 0-.484 1.244h3.016a1.839 1.839 0 0 0-.4-1.247 1.356 1.356 0 0 0-1.055-.423Zm6.071 5.816a2.314 2.314 0 0 1-1.941-.9 4.05 4.05 0 0 1-.7-2.529 4.064 4.064 0 0 1 .706-2.544 2.337 2.337 0 0 1 1.956-.91 2.291 2.291 0 0 1 1.995.967h.072a8.463 8.463 0 0 1-.1-1.129V6.842h1.418v9.348h-1.105l-.246-.871h-.066a2.257 2.257 0 0 1-1.989.991Zm.378-1.141a1.537 1.537 0 0 0 1.268-.49 2.578 2.578 0 0 0 .409-1.589v-.2a2.978 2.978 0 0 0-.409-1.784 1.516 1.516 0 0 0-1.28-.529 1.3 1.3 0 0 0-1.148.6 3.094 3.094 0 0 0-.4 1.721 3.017 3.017 0 0 0 .391 1.685 1.318 1.318 0 0 0 1.169.586Zm10.408-5.744a2.306 2.306 0 0 1 1.938.9 4.063 4.063 0 0 1 .693 2.534 4.076 4.076 0 0 1-.7 2.544 2.328 2.328 0 0 1-1.953.91 2.327 2.327 0 0 1-1.959-.907h-.1l-.258.787h-1.056V6.842h1.412v2.223q0 .246-.024.733t-.036.619h.06a2.242 2.242 0 0 1 1.983-.992Zm-.367 1.154a1.435 1.435 0 0 0-1.229.5 2.918 2.918 0 0 0-.387 1.68v.1a3.108 3.108 0 0 0 .385 1.757 1.43 1.43 0 0 0 1.253.543 1.273 1.273 0 0 0 1.141-.6 3.155 3.155 0 0 0 .388-1.718q-.001-2.262-1.551-2.262Zm2.767-1.033h1.538l1.352 3.767a7.147 7.147 0 0 1 .409 1.514h.048a6.313 6.313 0 0 1 .2-.8q.144-.472 1.526-4.479h1.526l-2.844 7.525a2.7 2.7 0 0 1-2.583 2.073 4.066 4.066 0 0 1-.913-.1v-1.12a3.309 3.309 0 0 0 .727.072 1.489 1.489 0 0 0 1.436-1.183l.246-.625ZM54.976 29.519h-1.442v-3.96h-4.05v3.96h-1.435v-8.783h1.435v3.593h4.05v-3.593h1.442Zm7.438-3.334a3.627 3.627 0 0 1-.835 2.541 2.992 2.992 0 0 1-2.325.913 3.186 3.186 0 0 1-1.64-.42 2.776 2.776 0 0 1-1.1-1.208 4.113 4.113 0 0 1-.385-1.826 3.6 3.6 0 0 1 .827-2.526 3.006 3.006 0 0 1 2.337-.9 2.927 2.927 0 0 1 2.283.928 3.585 3.585 0 0 1 .838 2.498Zm-4.836 0q0 2.3 1.7 2.3 1.682 0 1.682-2.3 0-2.277-1.694-2.277a1.445 1.445 0 0 0-1.289.589 3 3 0 0 0-.396 1.688Zm7.075-.06-2.229-3.25h1.61l1.514 2.325 1.526-2.325h1.6l-2.235 3.25 2.349 3.394h-1.6l-1.64-2.487-1.634 2.487h-1.6Zm7.161 2.373a3.434 3.434 0 0 0 1.033-.162v1.063a2.85 2.85 0 0 1-.6.171 4.16 4.16 0 0 1-.766.069q-2.007 0-2.007-2.115v-3.58h-.907v-.625l.973-.517.474-1.405h.871v1.478h1.893v1.069H70.89v3.557a1 1 0 0 0 .255.754.931.931 0 0 0 .669.243Zm7.64-2.313a3.627 3.627 0 0 1-.835 2.541 2.992 2.992 0 0 1-2.325.913 3.186 3.186 0 0 1-1.646-.421 2.776 2.776 0 0 1-1.1-1.208 4.113 4.113 0 0 1-.385-1.826 3.6 3.6 0 0 1 .831-2.525 3.006 3.006 0 0 1 2.337-.9 2.927 2.927 0 0 1 2.283.928 3.585 3.585 0 0 1 .841 2.498Zm-4.836 0q0 2.3 1.7 2.3 1.682 0 1.682-2.3 0-2.277-1.694-2.277a1.445 1.445 0 0 0-1.289.589 3 3 0 0 0-.403 1.688Zm11.69 3.334H84.89v-4.085a1.788 1.788 0 0 0-.309-1.147 1.2 1.2 0 0 0-.982-.378 1.562 1.562 0 0 0-1.31.529 2.919 2.919 0 0 0-.415 1.772v3.31h-1.412v-6.645h1.105l.2.871h.072a1.966 1.966 0 0 1 .853-.733 2.859 2.859 0 0 1 1.222-.254 2.132 2.132 0 0 1 2.391 2.433Zm7.878 0-2.548-7.335h-.048q.1 1.634.1 3.064v4.271h-1.3v-8.783h2.024l2.439 6.987h.036l2.511-6.987h2.031v8.783h-1.383v-4.343q0-.655.033-1.706t.057-1.274h-.048l-2.637 7.323Zm8.2 0h-1.408v-6.644h1.412Zm-1.5-8.4a.782.782 0 0 1 .207-.583.808.808 0 0 1 .592-.2.791.791 0 0 1 .58.2.782.782 0 0 1 .207.583.767.767 0 0 1-.207.568.784.784 0 0 1-.58.207.8.8 0 0 1-.592-.207.767.767 0 0 1-.199-.573Zm4.377 5.011-2.229-3.25h1.61l1.514 2.325 1.526-2.325h1.6l-2.235 3.25 2.349 3.394h-1.6l-1.64-2.487-1.626 2.482h-1.6Z'
                data-name='Path 43883'
              />
              <path
                fill={fill2 || 'white'}
                stroke='rgba(0,0,0,0)'
                strokeMiterlimit='10'
                d='M11.846 18.518a14.14 14.14 0 1 1 14.14 14.14 14.14 14.14 0 0 1-14.14-14.14Zm14.14 11.841a11.852 11.852 0 1 0-9.5-4.778c1.169-1.075 2.243-2.1 3.3-3.127-4.34-5.049-2.525-8.654.824-11.946l2.36 2.376 2.441-2.565a1.892 1.892 0 1 1 2.741 2.609l-2.515 2.642 1.494 1.5 1.638 1.649 2.44-2.564a1.892 1.892 0 1 1 2.737 2.613l-2.514 2.642 2.216 2.232c-3.262 3.271-6.818 5.044-11.711.937l-.005.007a137.324 137.324 0 0 1-3.349 3.171 11.784 11.784 0 0 0 7.403 2.602Z'
                data-name='Union 15'
              />
            </g>
          </svg>
        </>
      )
    }
    case 'hoxton-mix-symbol': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || 164}
            height={height || 164}
            viewBox='0 0 164 164'
            fill='none'
          >
            <g clipPath='url(#a)'>
              <path
                fill={fill || '#fff'}
                d='M.5 82a81.5 81.5 0 1 1 163 0 81.5 81.5 0 0 1-163 0ZM82 150.249a68.322 68.322 0 0 0 42.388-14.69A68.31 68.31 0 0 0 112.761 20.91a68.312 68.312 0 0 0-85.504 101.803 858.171 858.171 0 0 0 19-18.021v-.012c-25.009-29.1-14.555-49.872 4.746-68.85l13.6 13.7 14.07-14.782a10.905 10.905 0 0 1 15.478-.449 10.906 10.906 0 0 1 .329 15.481L79.986 65.006l8.614 8.673 9.44 9.507 14.065-14.781a10.9 10.9 0 0 1 7.649-3.458 10.902 10.902 0 0 1 10.301 6.503 10.899 10.899 0 0 1-2.15 11.99l-14.491 15.228 12.775 12.862c-18.8 18.854-39.3 29.069-67.5 5.4-.012.017-.017.025-.03.041-6.759 6.6-13.088 12.6-19.3 18.277a67.92 67.92 0 0 0 42.641 15v.001Z'
              />
            </g>
            <defs>
              <clipPath id='a'>
                <path fill='#fff' d='M0 0h164v164H0z' />
              </clipPath>
            </defs>
          </svg>
        </>
      )
    }
    case 'tick': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            viewBox='0 0 24 24'
            xmlns='http://www.w3.org/2000/svg'
            width={width || '24'}
            height={height || '24'}
          >
            <path fill='none' d='M0 0h24v24H0Z' />
            <path
              fill={fill || '#1d1d1f'}
              d='m8.6 22.471-1.891-3.179-3.608-.82.341-3.69L1 12.001 3.442 9.21l-.341-3.678 3.608-.811L8.6 1.54 12 2.999l3.4-1.459 1.89 3.179 3.611.821-.342 3.68 2.44 2.779-2.44 2.781.342 3.68-3.611.818-1.89 3.182-3.4-1.462-3.4 1.471Zm-1.19-11.17-1.409 1.41 4 4 8-8-1.412-1.419-6.588 6.588-2.591-2.578Z'
            />
          </svg>
        </>
      )
    }
    case 'close': {
      return (
        <svg
          width='22'
          height='22'
          viewBox='0 0 22 22'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M9.59829 11.0123L3.30529 4.71929C3.20978 4.62704 3.1336 4.5167 3.08119 4.39469C3.02878 4.27269 3.00119 4.14147 3.00004 4.00869C2.99888 3.87591 3.02419 3.74423 3.07447 3.62133C3.12475 3.49844 3.199 3.38679 3.29289 3.29289C3.38679 3.199 3.49844 3.12475 3.62133 3.07447C3.74423 3.02419 3.87591 2.99888 4.00869 3.00004C4.14147 3.00119 4.27269 3.02878 4.39469 3.08119C4.5167 3.1336 4.62704 3.20978 4.71929 3.30529L11.0123 9.59829L17.3053 3.30529C17.4939 3.12313 17.7465 3.02234 18.0087 3.02461C18.2709 3.02689 18.5217 3.13206 18.7071 3.31747C18.8925 3.50288 18.9977 3.75369 19 4.01589C19.0022 4.27808 18.9014 4.53069 18.7193 4.71929L12.4263 11.0123L18.7193 17.3053C18.9014 17.4939 19.0022 17.7465 19 18.0087C18.9977 18.2709 18.8925 18.5217 18.7071 18.7071C18.5217 18.8925 18.2709 18.9977 18.0087 19C17.7465 19.0022 17.4939 18.9014 17.3053 18.7193L11.0123 12.4263L4.71929 18.7193C4.53069 18.9014 4.27808 19.0022 4.01589 19C3.75369 18.9977 3.50288 18.8925 3.31747 18.7071C3.13206 18.5217 3.02689 18.2709 3.02461 18.0087C3.02234 17.7465 3.12313 17.4939 3.30529 17.3053L9.59829 11.0123Z'
            fill='#1D1D1F'
          />
        </svg>
      )
    }
    case 'close-circle': {
      return (
        <>
          <svg
            {...rest}
            width={width || 24}
            height={height || 24}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z'
              fill='#FF453A'
            />
            <path
              d='M12 10.667L14.666 8L16 9.33396L13.333 12L16 14.666L14.666 16L12 13.333L9.33396 16L8 14.666L10.667 12L8 9.33396L9.33396 8L12 10.667Z'
              fill='white'
            />
          </svg>
        </>
      )
    }
    case 'chevron-right': {
      return (
        <>
          <svg
            style={{
              // marginLeft: (width || 9) / 10,
              marginLeft: 0.75,
            }}
            {...rest}
            className={className}
            width={width || 15}
            height={height || 15}
            viewBox='0 0 15 15'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
          >
            <g>
              <path
                fill={fill || '#1D1D1F'}
                d='m4.847.305 6.933 6.686a.7.7 0 0 1 0 1.018l-6.933 6.686a1.112 1.112 0 0 1-1.53 0 1.018 1.018 0 0 1 0-1.475l5.93-5.72-5.931-5.718a1.019 1.019 0 0 1 0-1.476 1.112 1.112 0 0 1 1.53 0'
              />
            </g>
          </svg>
        </>
      )
    }
    case 'chevron-left': {
      return (
        <>
          <svg
            style={{
              // marginLeft: (width || 9) / 10,
              marginRight: 0.75,
            }}
            {...rest}
            className={className}
            width={width || 15}
            height={height || 15}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 15 15'
            fill='none'
          >
            <g>
              <path
                fill={fill || '#1D1D1F'}
                d='M10.153 14.695 3.22 8.009a.7.7 0 0 1 0-1.018L10.153.305a1.112 1.112 0 0 1 1.53 0 1.017 1.017 0 0 1 0 1.475L5.754 7.5l5.931 5.718a1.019 1.019 0 0 1 0 1.476 1.112 1.112 0 0 1-1.53 0'
              />
            </g>
          </svg>
        </>
      )
    }
    case 'chevron-down': {
      return (
        <svg
          {...rest}
          className={className}
          width={width || 24}
          height={height || 24}
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M12 15.0508C11.7695 15.0508 11.5586 14.957 11.3672 14.7695L6.95508 10.2578C6.88086 10.1797 6.82422 10.0957 6.78516 10.0059C6.75 9.91602 6.73242 9.81641 6.73242 9.70703C6.73242 9.5625 6.76758 9.42969 6.83789 9.30859C6.9082 9.1875 7.00195 9.0918 7.11914 9.02148C7.24023 8.95117 7.37305 8.91602 7.51758 8.91602C7.73633 8.91602 7.92773 8.99609 8.0918 9.15625L12.2695 13.4453L11.7363 13.4453L15.9023 9.15625C16.0664 8.99609 16.2578 8.91602 16.4766 8.91602C16.6211 8.91602 16.752 8.95117 16.8691 9.02148C16.9902 9.0918 17.0859 9.1875 17.1562 9.30859C17.2266 9.42969 17.2617 9.5625 17.2617 9.70703C17.2617 9.92187 17.1855 10.1035 17.0332 10.252L12.6328 14.7695C12.5391 14.8633 12.4395 14.9336 12.334 14.9805C12.2324 15.0234 12.1211 15.0469 12 15.0508Z'
            fill='#1D1D1F'
          />
        </svg>
      )
    }
    case 'chevron-up': {
      return (
        <svg
          {...rest}
          className={className}
          width={width || 24}
          height={height || 24}
          viewBox='0 0 24 24'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M12 8.94922C12.2305 8.94922 12.4414 9.04297 12.6328 9.23047L17.0449 13.7422C17.1191 13.8203 17.1758 13.9043 17.2148 13.9941C17.25 14.084 17.2676 14.1836 17.2676 14.293C17.2676 14.4375 17.2324 14.5703 17.1621 14.6914C17.0918 14.8125 16.998 14.9082 16.8809 14.9785C16.7598 15.0488 16.627 15.084 16.4824 15.084C16.2637 15.084 16.0723 15.0039 15.9082 14.8437L11.7305 10.5547L12.2637 10.5547L8.09766 14.8437C7.93359 15.0039 7.74219 15.084 7.52344 15.084C7.37891 15.084 7.24805 15.0488 7.13086 14.9785C7.00977 14.9082 6.91406 14.8125 6.84375 14.6914C6.77344 14.5703 6.73828 14.4375 6.73828 14.293C6.73828 14.0781 6.81445 13.8965 6.9668 13.748L11.3672 9.23047C11.4609 9.13672 11.5605 9.06641 11.666 9.01953C11.7676 8.97656 11.8789 8.95312 12 8.94922Z'
            fill='#1D1D1F'
          />
        </svg>
      )
    }
    case 'hamburger-menu': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            width='32'
            height='37'
            viewBox='0 0 32 37'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M23 11.5C23.2652 11.5 23.5196 11.6054 23.7071 11.7929C23.8946 11.9804 24 12.2348 24 12.5C24 12.7652 23.8946 13.0196 23.7071 13.2071C23.5196 13.3946 23.2652 13.5 23 13.5L9 13.5C8.73478 13.5 8.48043 13.3946 8.29289 13.2071C8.10536 13.0196 8 12.7652 8 12.5C8 12.2348 8.10536 11.9804 8.29289 11.7929C8.48043 11.6054 8.73478 11.5 9 11.5L23 11.5ZM23 17.5C23.2652 17.5 23.5196 17.6054 23.7071 17.7929C23.8946 17.9804 24 18.2348 24 18.5C24 18.7652 23.8946 19.0196 23.7071 19.2071C23.5196 19.3946 23.2652 19.5 23 19.5L15 19.5C14.7348 19.5 14.4804 19.3946 14.2929 19.2071C14.1054 19.0196 14 18.7652 14 18.5C14 18.2348 14.1054 17.9804 14.2929 17.7929C14.4804 17.6054 14.7348 17.5 15 17.5L23 17.5ZM23 23.5C23.2652 23.5 23.5196 23.6054 23.7071 23.7929C23.8946 23.9804 24 24.2348 24 24.5C24 24.7652 23.8946 25.0196 23.7071 25.2071C23.5196 25.3946 23.2652 25.5 23 25.5L9 25.5C8.73478 25.5 8.48043 25.3946 8.29289 25.2071C8.10536 25.0196 8 24.7652 8 24.5C8 24.2348 8.10536 23.9804 8.29289 23.7929C8.48043 23.6054 8.73478 23.5 9 23.5L23 23.5Z'
              fill='#1D1D1F'
            />
          </svg>
        </>
      )
    }
    case 'star': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || 24}
            height={height || 22}
            viewBox='0 0 22 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g>
              <path
                d='M11.0038 0L14.3076 6.26389L21.2858 7.47083L16.3472 12.5469L17.3556 19.5556L11.0038 16.4313L4.64826 19.5556L5.6566 12.5469L0.721874 7.47083L7.69618 6.26389L11.0038 0Z'
                fill={fill || 'white'}
              />
            </g>
          </svg>
        </>
      )
    }
    case 'basket': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || 24}
            height={height || 24}
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              fill='#1D1D1F'
              d='M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2Zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12Z'
            />
          </svg>
        </>
      )
    }
    case 'gift': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '18.114'}
            height={height || '18.003'}
            viewBox='0 0 18.114 18.003'
          >
            <path
              id='gift-111'
              d='M135.11,122.225a1.409,1.409,0,0,1-1.428,0l-3.349-2.21a.74.74,0,0,0-1.095.3l-.8,1.615a.49.49,0,0,0,.473.769h11.045a.478.478,0,0,0,.459-.761l-.846-1.63a.757.757,0,0,0-1.11-.29Zm6.858,14.893V127.1a.35.35,0,0,0-.363-.374h-6.618v11.16h6.219A.755.755,0,0,0,141.968,137.118Zm-14.316.766h6.081v-11.16h-6.522a.358.358,0,0,0-.363.374c0,.185,0,9.82,0,10.02A.762.762,0,0,0,127.652,137.884Zm-2.3-14.365v2.42a.366.366,0,0,0,.363.374h8.023v-3.167H125.71A.35.35,0,0,0,125.347,123.519Zm17.72-.374h-8.079v3.167H143.1a.377.377,0,0,0,.363-.374v-2.42A.375.375,0,0,0,143.067,123.145Z'
              transform='translate(-125.347 -119.881)'
              fill={fill || '#1d1d1f'}
            />
          </svg>
        </>
      )
    }
    case 'tag': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '20'}
            height={height || '20'}
            viewBox='0 0 20 20'
          >
            <path
              id='ic_loyalty_24px'
              d='M21.41,11.58l-9-9A1.987,1.987,0,0,0,11,2H4A2.006,2.006,0,0,0,2,4v7a2,2,0,0,0,.59,1.42l9,9A1.987,1.987,0,0,0,13,22a1.955,1.955,0,0,0,1.41-.59l7-7A1.955,1.955,0,0,0,22,13,2.02,2.02,0,0,0,21.41,11.58ZM5.5,7A1.5,1.5,0,1,1,7,5.5,1.5,1.5,0,0,1,5.5,7Z'
              transform='translate(-2 -2)'
              fill={fill || '#e31d86'}
            />
          </svg>
        </>
      )
    }
    case 'info': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '14'}
            height={height || '14'}
            viewBox='0 0 14 14'
          >
            <g id='info-icon' transform='translate(0 0)'>
              <path
                id='Path_43475'
                data-name='Path 43475'
                d='M11.953,2.054a7,7,0,1,0-.006,9.9A7,7,0,0,0,11.953,2.054ZM7.977,11.442a.2.2,0,0,1-.2.2H6.218a.2.2,0,0,1-.2-.2V5.637a.2.2,0,0,1,.2-.2H7.782a.2.2,0,0,1,.2.2ZM7,4.63A1.134,1.134,0,1,1,8.134,3.5,1.135,1.135,0,0,1,7,4.63Z'
                transform='translate(0)'
                fill={fill || '#1d1d1f'}
              />
            </g>
          </svg>
        </>
      )
    }
    case 'envelope': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '16'}
            height={height || '14'}
            viewBox='0 0 16 14'
          >
            <path
              id='mail-icon'
              d='M13.993,15H2.007A2.009,2.009,0,0,1,0,13V3A2,2,0,0,1,2.008,1H13.993A2.009,2.009,0,0,1,16,3V13a2,2,0,0,1-2.008,2ZM14,13V4.47L9.055,9.56a1.5,1.5,0,0,1-2.123,0L2,4.484V13ZM12.63,3H3.36L7.994,7.78Z'
              transform='translate(0 -1)'
              fill={fill || '#939393'}
              fillRule='evenodd'
            />
          </svg>
        </>
      )
    }
    case 'tick-circle': {
      return (
        <>
          <svg
            {...rest}
            width={width || '24'}
            height={width || '24'}
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z'
              fill='#30DB5B'
            />
            <path
              d='M10.243 16.485L6 12.242L7.414 10.828L10.243 13.657L15.899 8L17.314 9.414L10.243 16.485Z'
              fill='white'
            />
          </svg>
        </>
      )
    }
    case 'thumbs-up': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '17.5'}
            height={height || '18.002'}
            viewBox='0 0 17.5 18.002'
          >
            <g id='like-svgrepo-com' transform='translate(-6.675)'>
              <path
                id='Path_43516'
                data-name='Path 43516'
                d='M23.65,12.239a2.236,2.236,0,0,0,.523-1.495,2.3,2.3,0,0,0-.489-1.3,2.012,2.012,0,0,0-.478-2.315,4.157,4.157,0,0,0-3.023-.723,11.814,11.814,0,0,0-1.852.237h0c-.188.034-.388.075-.591.12a6.568,6.568,0,0,1,.471-2.187c.527-1.6.5-2.831-.1-3.652A2.393,2.393,0,0,0,16.192,0a.945.945,0,0,0-.727.331,2.533,2.533,0,0,0-.316,1.8c-.5,1.333-1.89,4.6-3.068,5.508a.4.4,0,0,0-.06.053,4.044,4.044,0,0,0-.738,1.107,1.554,1.554,0,0,0-.745-.188h-2.3a1.567,1.567,0,0,0-1.566,1.566V16.29a1.567,1.567,0,0,0,1.566,1.566h2.3a1.563,1.563,0,0,0,.9-.286l.885.105a37.9,37.9,0,0,0,5.018.275c.448.034.87.053,1.261.053a8.447,8.447,0,0,0,1.751-.158,3.274,3.274,0,0,0,2.338-1.453,2.3,2.3,0,0,0,.256-1.442A2.4,2.4,0,0,0,23.8,13,2.543,2.543,0,0,0,23.65,12.239Zm-15.409,4.6a.55.55,0,0,1-.55-.55V10.168a.55.55,0,0,1,.55-.55h2.3a.55.55,0,0,1,.55.55v6.118a.55.55,0,0,1-.55.55h-2.3Z'
                fill={fill || '#1d1d1f'}
              />
            </g>
          </svg>
        </>
      )
    }
    case 'bubble': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '22'}
            height={height || '22'}
            viewBox='0 0 22 22'
          >
            <path
              id='bubble-icon'
              d='M10.99,19.248A8.259,8.259,0,1,1,18.1,15.2a1.388,1.388,0,0,0-.137,1.082l.69,2.341-2.362-.674a1.374,1.374,0,0,0-1.082.138,8.235,8.235,0,0,1-4.217,1.159Zm9.254,2.7a1.376,1.376,0,0,0,1.7-1.7c-.137-.481-1.192-4.162-1.191-4.174a11,11,0,1,0-4.689,4.691l4.18,1.186h0Z'
              transform='translate(0 -0.001)'
              fill={fill || '#1d1d1f'}
              fillRule='evenodd'
            />
          </svg>
        </>
      )
    }
    case 'go-cardless': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '74'}
            height={height || '23.95'}
            viewBox='0 0 74 23.95'
          >
            <path
              id='Path_370'
              data-name='Path 370'
              d='M43.316,72.965h3.838s3.8-.077,3.762,3.377c-.038,2.572-3.684,2.955-3.684,2.955H43.317V72.965ZM54.8,85.363c.108.5.471,1.056,1.683,1.113a4.651,4.651,0,0,0,2.533-.576v1.19a16.321,16.321,0,0,1-2.61.423c-.883,0-3.493-.192-3.493-2.61,0-1.957,2.15-2.571,3.377-2.571,2.149.077,3.186.845,3.147,3.032,0,.032-3.536.008-4.638,0Zm.005-.96h2.945s0-1.113-1.42-1.113A1.346,1.346,0,0,0,54.8,84.4Zm9.316-1.2a3.012,3.012,0,0,1,2.342-.832A2.519,2.519,0,0,1,69.186,84.9c-.038.537-.23,2.571-2.993,2.571a2.61,2.61,0,0,1-2.073-.8v.645H62.239V80.411h1.881V83.2h0Zm1.613,3.2a1.646,1.646,0,0,0,1.612-1.458c0-.461-.269-1.42-1.689-1.42a1.506,1.506,0,0,0-1.535,1.239v.5A1.469,1.469,0,0,0,65.732,86.4Zm16.236-.192.038,1.075a8.818,8.818,0,0,1-1.458.192c-.576,0-2.073-.345-2.073-1.8V83.443H76.9v-1h1.573v-1.19h1.92v1.19h1.574v1H80.393v1.92s0,.96.768.96S81.967,86.207,81.967,86.207ZM76.133,74h2.226v5.335h1.88V74H82.5V72.888H76.133Zm-.768.269.23-1.113a7.137,7.137,0,0,0-2.648-.422c-1.651,0-4.3.921-4.3,3.416,0,2.456,2.8,3.3,4.529,3.3a6.266,6.266,0,0,0,2.3-.269l-.192-1.228a7.917,7.917,0,0,1-2.073.23c-1.267-.038-2.571-1.036-2.571-2.149a2.6,2.6,0,0,1,2.687-2.111,6.158,6.158,0,0,1,2.033.345ZM58.4,72.927s3.147-.153,3.147,1.689c0,1.574-1.8,1.689-1.8,1.689a1.191,1.191,0,0,1,.729.422c.192.307,1.458,2.61,1.458,2.61h-1.92a6.677,6.677,0,0,1-.461-.652c-.115-.269-.461-.845-.461-.845a1.316,1.316,0,0,0-1.19-.96l-.691-.025v2.482H55.368v-6.41H58.4Zm-1.19,2.879h1s1.381-.077,1.458-.921c.039-.806-1.343-.921-1.343-.921H57.21v1.843Zm7.715,1H68v-1.19H64.925V74.155h3.34V72.927H63.045v6.41H68.3V78.185H64.925V76.8h0Zm7.484,4.912H74.29V80.411H72.409Zm0,5.6H74.29V82.446H72.409ZM51.914,79.337h1.842v-6.41H51.914Zm-8.6,1.612h3.838s3.8-.077,3.762,3.377c-.038,2.571-3.684,2.956-3.684,2.956H43.317V80.949h0ZM45.2,86.092h1.228s2.533-.154,2.533-2.111-2.687-2.149-2.687-2.149H45.2Zm0-7.984h1.228s2.533-.192,2.533-2.111c0-2-2.687-2.149-2.687-2.149H45.2ZM26.083,76.781V89.623s-7.446-1.344-7.446-6.448C18.637,79.1,24.149,77.309,26.083,76.781Zm0-.24c-4.475-.324-14.623,1.774-14.623,6.365,0,2.993,5.374,7.369,14.124,7.484.345,0,10.286-.384,10.133-11.054-.129-8.962-6.836-10.532-8.981-10.8v-.546c8.551.543,15.276,5.685,15.276,11.926,0,6.6-7.523,11.975-16.773,11.975S8.5,86.513,8.5,79.912s7.484-11.975,16.735-11.975q.425,0,.845.015v8.589h0Z'
              transform='translate(-8.504 -67.937)'
              fillRule='evenodd'
            />
          </svg>
        </>
      )
    }
    case 'padlock': {
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '16'}
            height={height || '21'}
            viewBox='0 0 16 21'
          >
            <path
              id='ic_https_24px'
              d='M18,8H17V6A5,5,0,0,0,7,6V8H6a2.006,2.006,0,0,0-2,2V20a2.006,2.006,0,0,0,2,2H18a2.006,2.006,0,0,0,2-2V10A2.006,2.006,0,0,0,18,8Zm-6,9a2,2,0,1,1,2-2A2.006,2.006,0,0,1,12,17Zm3.1-9H8.9V6a3.1,3.1,0,0,1,6.2,0Z'
              transform='translate(-4 -1)'
              fill={fill || '#222'}
            />
          </svg>
        </>
      )
    }
    case 'privacy':
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || '18.645'}
            height={height || '23'}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 18.645 23'
          >
            <g
              id='Group_8513'
              data-name='Group 8513'
              transform='translate(3850.987 5920)'
            >
              <path
                id='user-icon'
                d='M11.279,4.283A4.283,4.283,0,1,0,4.212,7.538,5.991,5.991,0,0,0,1,12.849a.857.857,0,1,0,1.713,0,4.283,4.283,0,0,1,8.566,0,.857.857,0,1,0,1.713,0A5.992,5.992,0,0,0,9.78,7.538,4.273,4.273,0,0,0,11.279,4.283ZM7,6.853a2.57,2.57,0,1,1,2.57-2.57A2.57,2.57,0,0,1,7,6.853Z'
                transform='translate(-3848.596 -5914.252)'
                fill='#1d1d1f'
                fillRule='evenodd'
              />
              <path
                id='Path_43494'
                data-name='Path 43494'
                d='M-3803.4-5915.059c4.075-.307,6.449-1.627,8.115-3.448a13.212,13.212,0,0,0,8.39,3.448c0,2.164.6,6.825-.858,10.7-1.47,3.905-5.059,6.855-7.34,6.855-2.232,0-5.823-2.61-7.511-7.118C-3803.768-5907.717-3803.4-5912.517-3803.4-5915.059Z'
                transform='translate(-46.545 -0.493)'
                fill='none'
                stroke='#1d1d1f'
                strokeLinejoin='round'
                strokeWidth='2'
              />
            </g>
          </svg>
        </>
      )
    case 'arrow-down':
      return (
        <>
          <svg
            {...rest}
            className={className}
            width={width || '21'}
            height={height || '29.7'}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 210 297'
          >
            <g id='layer1'>
              <path
                style={{
                  fill: 'none',
                  stroke: 'transparent',
                  strokeLinecap: 'butt',
                  strokeLinejoin: 'miter',
                  strokeOpacity: 1,
                  strokeWidth: '2.4311831px',
                }}
                d='M 99.068483,1.8000542 V 261.81487 L 20.787714,185.37608 5.1315957,198.88332 108.27799,294.96907 206.51263,199.19031 192.08441,185.68307 117.48748,261.20089 117.79439,2.4140457 Z'
                id='path4529'
              />
              <path
                style={{
                  fill: fill || '#fffffe',
                  fillOpacity: 1,
                  strokeWidth: 1.01015258,
                }}
                d='M 218.13778,929.83607 27.051383,751.82216 30.861874,748.5591 c 2.09577,-1.79469 13.563375,-11.69205 25.483567,-21.99412 l 21.673074,-18.73103 4.647189,4.20034 c 2.555954,2.31019 70.019586,68.06724 149.919166,146.12678 79.89959,78.05954 145.67306,142.07793 146.16328,142.26313 0.51422,0.1943 0.89131,-208.93487 0.89131,-494.3119 V 11.463657 l 4.79822,0.571212 c 2.63903,0.314167 16.27913,0.869249 30.31134,1.233515 l 25.5131,0.662302 -0.67976,492.320794 c -0.37387,270.77644 -0.42483,492.32079 -0.11326,492.32079 0.31158,0 64.77083,-65.00331 143.24278,-144.45181 78.47195,-79.4485 143.01522,-144.45181 143.42948,-144.45181 1.21242,0 46.53645,42.67224 46.2252,43.52067 -0.15556,0.42403 -77.10494,75.62857 -170.99862,167.12119 -93.89368,91.49259 -175.53707,171.11779 -181.42978,176.94489 l -10.71399,10.5946 z'
                id='path4531'
                transform='scale(0.26458333)'
              />
            </g>
          </svg>
        </>
      )
    case 'institution':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '42'}
            height={height || '44'}
            viewBox='0 0 42 44'
            fill='none'
          >
            <path
              d='M9.94737 19.8H5.52632V35.2H9.94737V19.8ZM23.2105 19.8H18.7895V35.2H23.2105V19.8ZM42 39.6H0V44H42V39.6ZM36.4737 19.8H32.0526V35.2H36.4737V19.8ZM21 4.972L32.5168 11H9.48316L21 4.972ZM21 0L0 11V15.4H42V11L21 0Z'
              fill={fill || '#1D1D1F'}
            />
          </svg>
        </>
      )
    case 'accounting':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '44'}
            height={height || '44'}
            viewBox='0 0 44 44'
            fill='none'
          >
            <path
              d='M39.1111 0H4.88889C2.2 0 0 2.2 0 4.88889V39.1111C0 41.8 2.2 44 4.88889 44H39.1111C41.8 44 44 41.8 44 39.1111V4.88889C44 2.2 41.8 0 39.1111 0ZM39.1111 39.1111H4.88889V4.88889H39.1111V39.1111Z'
              fill='#1D1D1F'
            />
            <path
              d='M20.1667 11.5378H7.94444V15.2044H20.1667V11.5378Z'
              fill='#1D1D1F'
            />
            <path
              d='M36.6667 31.1667H24.4444V34.8333H36.6667V31.1667Z'
              fill='#1D1D1F'
            />
            <path
              d='M36.6667 25.0556H24.4444V28.7222H36.6667V25.0556Z'
              fill='#1D1D1F'
            />
            <path
              d='M12.2222 36.6667H15.8889V31.7778H20.7778V28.1111H15.8889V23.2222H12.2222V28.1111H7.33333V31.7778H12.2222V36.6667Z'
              fill='#1D1D1F'
            />
            <path
              d='M27.1089 19.4333L30.5556 15.9867L34.0022 19.4333L36.5933 16.8422L33.1467 13.3711L36.5933 9.92444L34.0022 7.33333L30.5556 10.78L27.1089 7.33333L24.5178 9.92444L27.9644 13.3711L24.5178 16.8422L27.1089 19.4333Z'
              fill='#1D1D1F'
            />
          </svg>
        </>
      )
    case 'tick-square':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '38'}
            height={height || '42'}
            viewBox='0 0 38 42'
            fill='none'
          >
            <path
              d='M38 2.0832V39.9168C37.9956 40.4683 37.7732 40.9959 37.381 41.3857C36.9887 41.7755 36.4581 41.9962 35.9037 42H2.09633C1.54072 42 1.00783 41.7806 0.614748 41.39C0.22167 40.9994 0.000559533 40.4695 0 39.9168V2.0832C0.00442204 1.5317 0.226771 1.00406 0.619015 0.614277C1.01126 0.224491 1.54191 0.00384309 2.09633 0H35.9037C37.0606 0 38 0.9324 38 2.0832ZM33.7778 4.2H4.22222V37.8H33.7778V4.2ZM17.5074 23.3541L26.4649 14.4459L29.45 17.4153L17.5074 29.295L9.29522 21.126L12.2824 18.1566L17.5074 23.3541Z'
              fill={fill || '#1D1D1F'}
            />
          </svg>
        </>
      )
    case 'data':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '38'}
            height={height || '34'}
            viewBox='0 0 38 34'
            fill='none'
          >
            <path
              d='M17.1 30.2222V11.3333H3.8V30.2222H17.1ZM17.1 7.55556V1.88889C17.1 1.38792 17.3002 0.907478 17.6565 0.553243C18.0128 0.199007 18.4961 0 19 0H36.1C36.6039 0 37.0872 0.199007 37.4435 0.553243C37.7998 0.907478 38 1.38792 38 1.88889V32.1111C38 32.6121 37.7998 33.0925 37.4435 33.4468C37.0872 33.801 36.6039 34 36.1 34H1.9C1.39609 34 0.912816 33.801 0.556497 33.4468C0.200178 33.0925 0 32.6121 0 32.1111V9.44444C0 8.94348 0.200178 8.46303 0.556497 8.1088C0.912816 7.75456 1.39609 7.55556 1.9 7.55556H17.1ZM20.9 3.77778V30.2222H34.2V3.77778H20.9ZM5.7 24.5556H15.2V28.3333H5.7V24.5556ZM22.8 24.5556H32.3V28.3333H22.8V24.5556ZM22.8 18.8889H32.3V22.6667H22.8V18.8889ZM22.8 13.2222H32.3V17H22.8V13.2222ZM5.7 18.8889H15.2V22.6667H5.7V18.8889Z'
              fill='#1D1D1F'
            />
          </svg>
        </>
      )
    case 'file-lock':
      return (
        <>
          <svg
            {...rest}
            className={className}
            xmlns='http://www.w3.org/2000/svg'
            width={width || '38'}
            height={height || '42'}
            viewBox='0 0 38 42'
            fill='none'
          >
            <path
              d='M25.3333 4.2H4.22222V37.8H33.7777V12.6H25.3333V4.2ZM0 2.0832C0 0.9324 0.943666 0 2.109 0H27.4444L38 10.5V39.8853C38.0019 40.1611 37.9492 40.4345 37.8449 40.6901C37.7406 40.9456 37.5867 41.1782 37.3921 41.3745C37.1974 41.5709 36.9658 41.7272 36.7104 41.8345C36.455 41.9418 36.1809 41.9981 35.9036 42H2.09633C1.54191 41.9962 1.01126 41.7755 0.619014 41.3857C0.226771 40.9959 0.00442203 40.4683 0 39.9168V2.0832ZM25.3333 18.9H27.4444V29.4H10.5555V18.9H12.6666V16.8C12.6666 15.1291 13.3339 13.5267 14.5216 12.3452C15.7094 11.1637 17.3203 10.5 19 10.5C20.6797 10.5 22.2906 11.1637 23.4783 12.3452C24.666 13.5267 25.3333 15.1291 25.3333 16.8V18.9ZM21.1111 18.9V16.8C21.1111 16.243 20.8887 15.7089 20.4928 15.3151C20.0968 14.9212 19.5599 14.7 19 14.7C18.4401 14.7 17.9031 14.9212 17.5072 15.3151C17.1113 15.7089 16.8889 16.243 16.8889 16.8V18.9H21.1111Z'
              fill='#1D1D1F'
            />
          </svg>
        </>
      )
    case 'check':
      return (
        <svg
          {...rest}
          className={className}
          xmlns='http://www.w3.org/2000/svg'
          width={width || '12'}
          height={height || '9'}
          fill='none'
          viewBox='0 0 12 9'
        >
          <path
            fill='#1977EC'
            d='M4.243 8.743 0 4.5l1.414-1.414 2.829 2.829L9.899.258l1.415 1.414z'
          ></path>
        </svg>
      )
    default:
      return null
  }
}

export default Icon
